import type { WorkflowJsonObject } from '@atlas/types'
import { HookCode, RequiredWorkOrderField } from '@atlas/types'

export interface HookCatalogEntry {
	code: HookCode
	label: string
	description: string
}

/** Guards evaluated before a transition executes — the builder's "Pre-hooks" attach menu. */
export const PRE_HOOK_CATALOG: readonly HookCatalogEntry[] = [
	{
		code: HookCode.CHECKLIST_COMPLETE,
		label: 'Checklist complete',
		description: 'A checklist template is fully checked off.',
	},
	{
		code: HookCode.DELIVERY_SLOTS_FILLED,
		label: 'Delivery slots filled',
		description: 'A named deliverable slot has an asset.',
	},
	{ code: HookCode.ARTIFACT_EXISTS, label: 'Artifact exists', description: 'A named generated artifact exists.' },
	{
		code: HookCode.CHANGE_REQUEST_CREATED,
		label: 'Change request created',
		description: 'An open change request exists on this lane.',
	},
	{
		code: HookCode.CHANGE_REQUEST_RESOLVED,
		label: 'Change request resolved',
		description: 'Every change request on this lane is resolved.',
	},
	{
		code: HookCode.ASSIGNEE_PRESENT,
		label: 'Assignee present',
		description: 'The work order has at least one assignee.',
	},
	{
		code: HookCode.FIELDS_PRESENT,
		label: 'Fields present',
		description: 'The work order has every field picked, such as asset type, complexity or due date.',
	},
]

/**
 * Actions run after a transition commits — the builder's "Post-hooks" attach menu.
 *
 * `emit_event` is deliberately ABSENT. Its `params.eventType` must name an
 * allow-listed publishable event type, that allow-list is currently empty, and so
 * every attach would be rejected at the write boundary. Offering a code the
 * administrator cannot successfully attach is worse than not offering it. It
 * returns here when the allow-list gains an entry. Rows already attached still
 * render and can still be detached: only `HOOK_CONFIG` and `hookCatalogLabel`
 * drive that, and both still cover the code.
 */
export const POST_HOOK_CATALOG: readonly HookCatalogEntry[] = [
	{ code: HookCode.NOTIFY, label: 'Notify', description: 'Posts to a webhook URL.' },
	{
		code: HookCode.LOG_MESSAGE,
		label: 'Log a message',
		description: 'Writes a log line after this transition commits. Diagnostic: proves post-hooks run here.',
	},
]

/** A hook code whose `params` is one free-text field — the config editor renders one input bound to `paramKey`. */
export interface HookTextConfig {
	kind: 'text'
	paramKey: string
	label: string
	placeholder: string
}

export interface HookChecklistConfig {
	kind: 'checklist'
}

/** `fields_present`: a pick of the work-order fields the move requires. */
export interface HookFieldsConfig {
	kind: 'fields'
}

/** What a hook code's `params` needs, matching `hookParamsSchemaByCode` in `@atlas/models`. `null` = empty params (no config UI). */
export type HookConfig = HookTextConfig | HookChecklistConfig | HookFieldsConfig | null

export const HOOK_CONFIG: Record<HookCode, HookConfig> = {
	[HookCode.CHECKLIST_COMPLETE]: { kind: 'checklist' },
	[HookCode.DELIVERY_SLOTS_FILLED]: { kind: 'text', paramKey: 'slot', label: 'Slot', placeholder: 'visual' },
	[HookCode.ARTIFACT_EXISTS]: {
		kind: 'text',
		paramKey: 'artifact',
		label: 'Artifact',
		placeholder: 'distribution_zip',
	},
	[HookCode.CHANGE_REQUEST_CREATED]: null,
	[HookCode.CHANGE_REQUEST_RESOLVED]: null,
	[HookCode.ASSIGNEE_PRESENT]: null,
	[HookCode.FIELDS_PRESENT]: { kind: 'fields' },
	[HookCode.NOTIFY]: { kind: 'text', paramKey: 'url', label: 'Webhook URL', placeholder: 'https://…' },
	// Not attachable (see POST_HOOK_CATALOG), but an existing row still renders
	// through this entry. The placeholder is NOT `work_order.status_changed`: that
	// value is permanently ineligible, being the trigger of hook execution itself.
	[HookCode.EMIT_EVENT]: {
		kind: 'text',
		paramKey: 'eventType',
		label: 'Event type',
		placeholder: 'none available yet',
	},
	// One editable field, the message. `delaySeconds` is optional and set through
	// the API when someone wants to prove the hook outlives the request that
	// triggered it; the default attach logs immediately.
	[HookCode.LOG_MESSAGE]: {
		kind: 'text',
		paramKey: 'message',
		label: 'Message',
		placeholder: 'post-hook ran',
	},
}

/** Labels for codes that exist and must render, but are not offered for new attachment. */
const WITHDRAWN_HOOK_LABELS: Partial<Record<HookCode, string>> = {
	[HookCode.EMIT_EVENT]: 'Emit event',
}

export const hookCatalogLabel = (code: HookCode): string =>
	[...PRE_HOOK_CATALOG, ...POST_HOOK_CATALOG].find((h) => h.code === code)?.label ?? WITHDRAWN_HOOK_LABELS[code] ?? code

/**
 * Attaching a hook must send params that already pass `hookParamsSchemaByCode`
 * (`@atlas/models`) — several codes reject `{}` outright (`checklist_complete`
 * requires a mode/template; `delivery_slots_filled`/`artifact_exists`/`notify`
 * require a non-empty field). This gives every attach a valid starting value —
 * `notify`'s in particular must be a well-formed URL, not just non-empty —
 * the admin edits it afterward via the inline config editor.
 */
export function defaultHookParams(code: HookCode): WorkflowJsonObject {
	const config = HOOK_CONFIG[code]
	if (!config) return {}
	if (config.kind === 'checklist') return { mode: 'dynamic' }
	if (config.kind === 'fields') return { fields: Object.values(RequiredWorkOrderField) }
	if (config.paramKey === 'url') return { url: 'https://example.com' }
	return { [config.paramKey]: config.placeholder }
}
